import { MyLiNKQueryKeys } from "constants/my-link-query-keys";
import UserBookmarkRecord from "models/view-models/user-bookmark-record";
import { useQuery } from "react-query";
import { useParams } from "react-router";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { t } from "utilities/localization-utils";
import UserBookmarkService, {
    ListBookmarksQueryParams,
    UserBookmarkListQueryParams,
} from "utilities/services/user-bookmarks/user-bookmark-service";
import UserCollectionService from "utilities/services/users/user-collections/user-collection-service";
import { ToastManager } from "utilities/toast/toast-manager";
export interface BookmarkQueryResponse {
    bookmarks: UserBookmarkRecord[];
    bookmarksLength: number;
}
export interface UseBookmarksByCollectionQueryOptions
    extends ListBookmarksQueryParams {}

export default function useBookmarksByCollectionQuery(
    options: UserBookmarkListQueryParams
) {
    const { groupId: collectionId } = useParams<{ groupId: string }>();
    const { currentIdentity } = useCurrentIdentity();
    const {
        data: bookmarks,
        isLoading,
        refetch: refetchAllBookmarks,
    } = useQuery({
        queryKey: [
            MyLiNKQueryKeys.BookmarksByCollectionId,
            collectionId,
            options.sortBy,
            options.take,
            options.skip,
            options.searchText,
        ],
        queryFn: async () => await fetchBookmarks(collectionId, options),
        keepPreviousData: true,
    });

    const { data: collection, refetch: refetchCollection } = useQuery({
        queryKey: [MyLiNKQueryKeys.CollectionDetail, collectionId],
        queryFn: async () =>
            await fetchCollection(collectionId, currentIdentity?.userId()!),
    });

    return {
        bookmarks,
        isLoading,
        refetchAllBookmarks,
        collection,
        refetchCollection,
    };
}

const fetchBookmarks = async (
    collectionId: string,
    { skip, take, sortBy, searchText }: UserBookmarkListQueryParams
) => {
    try {
        const result = await UserBookmarkService.list({
            collectionId: Number(collectionId),
            skip,
            take,
            sortBy,
            searchText,
        });

        if (result.result?.hasErrors()) {
            throw new Error();
        }
        return {
            bookmarks: result.resultObjects,
            bookmarksLength: result.rowCount,
        };
    } catch (err) {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("loading"),
                resource: t("bookmark_plural"),
            })
        );
    }
};

const fetchCollection = async (collectionId: string, userId: number) => {
    try {
        const result = await UserCollectionService.get({
            id: Number(collectionId),
            userId,
        });

        if (result.result?.hasErrors()) {
            throw new Error();
        }
        return result.resultObject;
    } catch (err) {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("loading"),
                resource: t("collection"),
            })
        );
    }
};
